import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  Ban,
  Camera,
  Check,
  CheckCircle2,
  Clock,
  Download,
  HourglassIcon,
  X,
} from 'lucide-react';
import { type ConfigChange, useConfigChanges } from '@/hooks/useConfigChanges';
import {
  type ConfigSnapshotListItem,
  useCaptureDeviceConfig,
  useConfigRestores,
  useConfigSnapshots,
} from '@/hooks/useConfigSnapshots';
import { configTimeline } from '@/utils/configTimeline';
import ConfigDiffViewer from './ConfigDiffViewer';
import ConfigSnapshotViewer from './ConfigSnapshotViewer';
import ConfigRestoreHistory, { RestoreTimelineItem } from './ConfigRestoreHistory';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';

interface ConfigTimelineProps {
  deviceId: string;
}

const STATUS_STYLE: Record<string, string> = {
  success: 'bg-sev-4/15 text-sev-4',
  partial_failure: 'bg-sev-3/20 text-foreground',
  failure: 'bg-sev-1/15 text-sev-1',
  rejected: 'bg-muted text-muted-foreground',
  pending: 'bg-sev-5/15 text-sev-5',
  timeout: 'bg-sev-3/20 text-foreground',
};

function statusIcon(status: string) {
  switch (status) {
    case 'success': return Check;
    case 'partial_failure': return AlertCircle;
    case 'failure': return X;
    case 'rejected': return Ban;
    case 'pending': return Clock;
    case 'timeout': return HourglassIcon;
    default: return Clock;
  }
}

/** How a snapshot came to be taken, as the timeline says it. */
const SOURCE_LABEL: Record<string, string> = {
  on_demand: 'manual',
  before_restore: 'before restore',
  after_restore: 'after restore',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export default function ConfigTimeline({ deviceId }: ConfigTimelineProps) {
  const { data: changes, isLoading: changesLoading, isError: changesError } = useConfigChanges(deviceId);
  const { data: scheduledSnapshots, isLoading: snapshotsLoading, isError: snapshotsError } = useConfigSnapshots(deviceId);
  const { data: restores } = useConfigRestores(deviceId);
  const captureMutation = useCaptureDeviceConfig(deviceId);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [diffChangeId, setDiffChangeId] = useState<string | null>(null);
  const [viewingSnapshotId, setViewingSnapshotId] = useState<string | null>(null);
  const [snapshotDiffPair, setSnapshotDiffPair] = useState<{ baseId: string; targetId: string } | null>(null);
  const [compareBaseId, setCompareBaseId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  const toggleDiff = (change: ConfigChange) => {
    setDiffChangeId(diffChangeId === change.id ? null : change.id);
  };
  const toggleSnapshotView = (snapshot: ConfigSnapshotListItem) => {
    setViewingSnapshotId(viewingSnapshotId === snapshot.id ? null : snapshot.id);
  };

  const hasDiffSnapshots = (change: ConfigChange): boolean =>
    !!(change.result?.preSnapshotId && change.result.postSnapshotId);

  return (
    <div className="space-y-6">
      <ConfigRestoreHistory deviceId={deviceId} />

      <section className="bg-card border border-border rounded-lg p-5">
        <h2 className="text-sm font-semibold mb-4">Configuration change history</h2>

        {changesLoading ? <Skeleton className="h-32 w-full" /> : null}
        {changesError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Failed to load config change history.</AlertDescription>
          </Alert>
        ) : null}

        {!changesLoading && !changesError && (!changes || changes.length === 0) ? (
          <EmptyState
            variant="no-data"
            title="No changes yet"
            description="Configuration changes made to this device will appear here."
            size="inline"
          />
        ) : null}

        {changes && changes.length > 0 ? (
          <ol className="relative space-y-4 pl-6 before:absolute before:left-3 before:top-1.5 before:bottom-1.5 before:w-px before:bg-border-subtle">
            {changes.map((change) => {
              const Icon = statusIcon(change.status);
              return (
                <li key={change.id} className="relative">
                  <span className="absolute -left-6 top-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-bg-subtle border border-border text-muted-foreground">
                    <Icon className="w-3 h-3" aria-hidden="true" />
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(change.createdAt), { addSuffix: true })}
                    </span>
                    <Badge
                      className={cn(
                        'border-0 font-normal',
                        STATUS_STYLE[change.status] ?? 'bg-muted text-muted-foreground',
                      )}
                    >
                      {change.status.replace(/_/g, ' ')}
                    </Badge>
                    <div className="ml-auto flex items-center gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => { toggleExpand(change.id); }}>
                        {expandedId === change.id ? 'Hide details' : 'Details'}
                      </Button>
                      {hasDiffSnapshots(change) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { toggleDiff(change); }}
                        >
                          {diffChangeId === change.id ? 'Hide diff' : 'Compare'}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {change.result ? (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {change.result.operationResults.map((op, i) => (
                        <Badge
                          key={i}
                          className={cn(
                            'border-0 font-normal',
                            op.status === 'success'
                              ? 'bg-sev-4/15 text-sev-4'
                              : op.status === 'failed'
                                ? 'bg-sev-1/15 text-sev-1'
                                : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {op.operationName}
                        </Badge>
                      ))}
                    </div>
                  ) : null}

                  {expandedId === change.id && change.result ? (
                    <div className="mt-3 rounded-md border border-border bg-bg-subtle p-3 space-y-2 text-sm">
                      <p>
                        <strong>Overall:</strong>{' '}
                        <Badge
                          className={cn(
                            'border-0 font-normal',
                            STATUS_STYLE[change.result.overallResult] ?? 'bg-muted text-muted-foreground',
                          )}
                        >
                          {change.result.overallResult.replace(/_/g, ' ')}
                        </Badge>
                      </p>
                      {change.completedAt ? (
                        <p className="text-xs text-muted-foreground">
                          Completed: {new Date(change.completedAt).toLocaleString()}
                        </p>
                      ) : null}
                      <table className="w-full text-xs mt-2">
                        <thead className="border-b border-border-subtle">
                          <tr className="text-muted-foreground uppercase tracking-wider">
                            <th className="text-left py-1.5 px-2 font-medium">Operation</th>
                            <th className="text-left py-1.5 px-2 font-medium">Status</th>
                            <th className="text-left py-1.5 px-2 font-medium">Protocol</th>
                            <th className="text-left py-1.5 px-2 font-medium">Output</th>
                            <th className="text-left py-1.5 px-2 font-medium">Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-subtle">
                          {change.result.operationResults.map((op, i) => (
                            <tr key={i}>
                              <td className="py-1.5 px-2">{op.operationName}</td>
                              <td className="py-1.5 px-2">
                                <Badge
                                  className={cn(
                                    'border-0 font-normal',
                                    op.status === 'success'
                                      ? 'bg-sev-4/15 text-sev-4'
                                      : op.status === 'failed'
                                        ? 'bg-sev-1/15 text-sev-1'
                                        : 'bg-muted text-muted-foreground',
                                  )}
                                >
                                  {op.status}
                                </Badge>
                              </td>
                              <td className="py-1.5 px-2 font-mono">{op.protocolUsed || '-'}</td>
                              <td className="py-1.5 px-2">
                                {op.output ? (
                                  <pre className="text-[11px] font-mono max-h-24 overflow-auto whitespace-pre-wrap">
                                    {op.output}
                                  </pre>
                                ) : '-'}
                              </td>
                              <td className="py-1.5 px-2 text-destructive">{op.error || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}

                  {diffChangeId === change.id &&
                  change.result?.preSnapshotId &&
                  change.result.postSnapshotId ? (
                    <div className="mt-3">
                      <ConfigDiffViewer
                        deviceId={deviceId}
                        preSnapshotId={change.result.preSnapshotId}
                        postSnapshotId={change.result.postSnapshotId}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : null}
      </section>

      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold">Config snapshots</h2>
          <Button
            size="sm"
            onClick={() => { captureMutation.mutate(); }}
            disabled={captureMutation.isPending}
          >
            <Download className="w-3.5 h-3.5" />
            {captureMutation.isPending ? 'Capturing…' : 'Capture config'}
          </Button>
        </div>

        {captureMutation.isSuccess && captureMutation.data.success ? (
          <Alert className="mb-3 border-sev-4 bg-sev-4/10">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>Config captured successfully.</AlertDescription>
          </Alert>
        ) : null}
        {captureMutation.isSuccess && !captureMutation.data.success ? (
          <Alert className="mb-3 border-sev-3 bg-sev-3-soft">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {captureMutation.data.error ?? 'Config capture returned no new snapshots.'}
              {captureMutation.data.errors.length > 0 ? (
                <ul className="mt-1 list-disc pl-5">
                  {captureMutation.data.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        {captureMutation.isError ? (
          <Alert variant="destructive" className="mb-3">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to capture config:{' '}
              {captureMutation.error instanceof Error
                ? captureMutation.error.message
                : 'Unknown error'}
            </AlertDescription>
          </Alert>
        ) : null}

        {snapshotsLoading ? <Skeleton className="h-32 w-full" /> : null}
        {snapshotsError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Failed to load config snapshots.</AlertDescription>
          </Alert>
        ) : null}

        {!snapshotsLoading && !snapshotsError && (!scheduledSnapshots || scheduledSnapshots.length === 0) ? (
          <EmptyState
            variant="no-data"
            title="No snapshots yet"
            description="Click 'Capture config' to collect the device's running configuration."
            size="inline"
          />
        ) : null}

        {scheduledSnapshots && scheduledSnapshots.length > 0 ? (
          <ol className="relative space-y-3 pl-6 before:absolute before:left-3 before:top-1.5 before:bottom-1.5 before:w-px before:bg-border-subtle">
            {configTimeline(scheduledSnapshots, restores ?? []).map((item) => {
              if (item.kind === 'restore') {
                return (
                  <RestoreTimelineItem key={`restore-${item.restore.id}`} restore={item.restore} target={item.target} />
                );
              }
              const { snapshot, index } = item;
              const isOldest = index === scheduledSnapshots.length - 1;
              const previousSnapshot = !isOldest ? scheduledSnapshots[index + 1] : null;
              const isBase = compareBaseId === snapshot.id;
              const isDiffTarget = snapshotDiffPair?.targetId === snapshot.id;
              return (
                <li key={snapshot.id} className="relative">
                  <span className="absolute -left-6 top-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-bg-subtle border border-border text-muted-foreground">
                    <Camera className="w-3 h-3" aria-hidden="true" />
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(snapshot.createdAt), { addSuffix: true })}
                    </span>
                    <Badge variant="secondary" className="font-normal text-[10px]">
                      {snapshot.format}
                    </Badge>
                    <Badge variant="secondary" className="font-normal text-[10px]">
                      {formatBytes(snapshot.sizeBytes)}
                    </Badge>
                    <Badge variant="secondary" className="font-normal text-[10px]">
                      {SOURCE_LABEL[snapshot.source] ?? snapshot.source}
                    </Badge>
                    {isBase ? (
                      <Badge className="bg-brand-soft text-brand border-0 font-normal text-[10px]">
                        Base
                      </Badge>
                    ) : null}
                    <div className="ml-auto flex items-center gap-1.5">
                      {isDiffTarget ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSnapshotDiffPair(null); }}
                        >
                          Hide diff
                        </Button>
                      ) : previousSnapshot && !compareBaseId ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSnapshotDiffPair({
                              baseId: previousSnapshot.id,
                              targetId: snapshot.id,
                            });
                          }}
                        >
                          Diff
                        </Button>
                      ) : null}
                      {isBase ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setCompareBaseId(null); }}
                        >
                          Cancel
                        </Button>
                      ) : compareBaseId && compareBaseId !== snapshot.id ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const baseIdx = scheduledSnapshots.findIndex(
                              (s) => s.id === compareBaseId,
                            );
                            if (baseIdx > index) {
                              setSnapshotDiffPair({
                                baseId: compareBaseId,
                                targetId: snapshot.id,
                              });
                            } else {
                              setSnapshotDiffPair({
                                baseId: snapshot.id,
                                targetId: compareBaseId,
                              });
                            }
                            setCompareBaseId(null);
                          }}
                        >
                          Compare
                        </Button>
                      ) : !compareBaseId ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setCompareBaseId(snapshot.id); }}
                        >
                          Select base
                        </Button>
                      ) : null}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { toggleSnapshotView(snapshot); }}
                      >
                        {viewingSnapshotId === snapshot.id ? 'Hide' : 'View'}
                      </Button>
                    </div>
                  </div>

                  {viewingSnapshotId === snapshot.id ? (
                    <div className="mt-2">
                      <ConfigSnapshotViewer
                        deviceId={deviceId}
                        snapshotId={snapshot.id}
                        onClose={() => { setViewingSnapshotId(null); }}
                      />
                    </div>
                  ) : null}

                  {snapshotDiffPair && snapshotDiffPair.targetId === snapshot.id ? (
                    <div className="mt-2">
                      <ConfigDiffViewer
                        deviceId={deviceId}
                        preSnapshotId={snapshotDiffPair.baseId}
                        postSnapshotId={snapshotDiffPair.targetId}
                        onClose={() => { setSnapshotDiffPair(null); }}
                        baseLabel={(() => {
                          const base = scheduledSnapshots.find(
                            (s) => s.id === snapshotDiffPair.baseId,
                          );
                          return base
                            ? formatDistanceToNow(new Date(base.createdAt), { addSuffix: true })
                            : undefined;
                        })()}
                        targetLabel={formatDistanceToNow(new Date(snapshot.createdAt), {
                          addSuffix: true,
                        })}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : null}
      </section>
    </div>
  );
}
